import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LanguageSelector from '../components/LanguageSelector';

const { FiArrowLeft, FiShield, FiDatabase, FiMail, FiServer, FiGlobe, FiExternalLink, FiUser, FiClock, FiMapPin } = FiIcons;

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();

  // Format date based on current language
  const getFormattedDate = () => {
    const date = new Date('2026-01-25');
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : i18n.language, options);
  };

  const services = [
    {
      icon: FiDatabase,
      name: 'Supabase',
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
      policyUrl: 'https://supabase.com/privacy',
    },
    {
      icon: FiMail,
      name: 'Resend',
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      policyUrl: 'https://resend.com/legal/privacy-policy',
    },
    {
      icon: FiServer,
      name: 'Hostinger',
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
      policyUrl: 'https://www.hostinger.com/privacy-policy',
    },
    {
      icon: FiGlobe,
      name: 'Grigora',
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30',
      policyUrl: 'https://www.grigora.co/privacy/',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto"
      >
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <Link
              to="/login"
              className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>{t('common.back')}</span>
            </Link>
            <LanguageSelector />
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
              <SafeIcon icon={FiShield} className="text-3xl text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
              {t('privacy.title')}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              {t('privacy.lastUpdated', { date: getFormattedDate() })}
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-8">
            <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
              {t('privacy.introduction')}
            </p>
          </section>

          {/* Data Controller */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
              <SafeIcon icon={FiUser} className="text-blue-600 dark:text-blue-400" />
              {t('privacy.dataController.title')}
            </h2>
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4">
              <p className="text-slate-700 dark:text-slate-300 mb-2">
                {t('privacy.dataController.description')}
              </p>
              <p className="text-slate-700 dark:text-slate-300">
                <span className="font-medium">{t('privacy.dataController.email')}</span>{' '}
                <span className="text-blue-600 dark:text-blue-400 font-mono">[PLACEHOLDER_EMAIL]</span>
              </p>
            </div>
          </section>

          {/* Data Collection */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">
              {t('privacy.dataCollection.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 mb-4">
              {t('privacy.dataCollection.description')}
            </p>
            <div className="space-y-3">
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
                <p className="text-slate-800 dark:text-white font-medium">
                  {t('privacy.dataCollection.items.email.data')}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('privacy.dataCollection.items.email.basis')}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
                <p className="text-slate-800 dark:text-white font-medium">
                  {t('privacy.dataCollection.items.tasks.data')}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('privacy.dataCollection.items.tasks.basis')}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
                <p className="text-slate-800 dark:text-white font-medium">
                  {t('privacy.dataCollection.items.preferences.data')}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('privacy.dataCollection.items.preferences.basis')}
                </p>
              </div>
            </div>
          </section>

          {/* Services Used */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">
              {t('privacy.services.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 mb-6">
              {t('privacy.services.description')}
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              {services.map((service, index) => (
                <motion.div
                  key={service.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`${service.bgColor} rounded-xl p-4 border border-slate-200 dark:border-slate-700`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <SafeIcon icon={service.icon} className={`text-xl ${service.color}`} />
                    <h3 className="font-semibold text-slate-800 dark:text-white">
                      {service.name}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                    {t(`privacy.services.${service.name.toLowerCase()}.description`)}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 mb-2">
                    <SafeIcon icon={FiMapPin} className="text-xs" />
                    <span>{t(`privacy.services.${service.name.toLowerCase()}.location`)}</span>
                  </div>
                  <a
                    href={service.policyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {t('privacy.services.viewPolicy')}
                    <SafeIcon icon={FiExternalLink} className="text-xs" />
                  </a>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
              <SafeIcon icon={FiClock} className="text-blue-600 dark:text-blue-400" />
              {t('privacy.retention.title')}
            </h2>
            <ul className="space-y-2 text-slate-700 dark:text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                {t('privacy.retention.items.account')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                {t('privacy.retention.items.deletion')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                {t('privacy.retention.items.logs')}
              </li>
            </ul>
          </section>

          {/* International Transfers */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">
              {t('privacy.transfers.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300">
              {t('privacy.transfers.description')}
            </p>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">
              {t('privacy.security.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300">
              {t('privacy.security.description')}
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">
              {t('privacy.rights.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 mb-4">
              {t('privacy.rights.description')}
            </p>
            <ul className="space-y-2 text-slate-700 dark:text-slate-300 mb-4">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                {t('privacy.rights.items.access')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                {t('privacy.rights.items.rectification')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                {t('privacy.rights.items.deletion')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                {t('privacy.rights.items.portability')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                {t('privacy.rights.items.withdraw')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                {t('privacy.rights.items.complaint')}
              </li>
            </ul>
            <p className="text-slate-700 dark:text-slate-300 text-sm italic">
              {t('privacy.rights.howToExercise')}
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">
              {t('privacy.contact.title')}
            </h2>
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-3">
              <div>
                <p className="text-slate-700 dark:text-slate-300 mb-1">
                  {t('privacy.contact.questions')}
                </p>
                <p className="text-slate-700 dark:text-slate-300">
                  <span className="font-medium">{t('privacy.dataController.email')}</span>{' '}
                  <span className="text-blue-600 dark:text-blue-400 font-mono">[PLACEHOLDER_EMAIL]</span>
                </p>
              </div>
              <div className="border-t border-slate-200 dark:border-slate-600 pt-3">
                <p className="text-slate-700 dark:text-slate-300 mb-2">
                  {t('privacy.contact.complaint')}
                </p>
                <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                  <li>
                    • France :{' '}
                    <a
                      href="https://www.cnil.fr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      CNIL (www.cnil.fr)
                    </a>
                  </li>
                  <li>• {t('privacy.contact.otherCountries')}</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Policy Changes */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">
              {t('privacy.changes.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300">
              {t('privacy.changes.description')}
            </p>
          </section>

          {/* Back to Login Button */}
          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-xl font-medium shadow-lg hover:shadow-xl transition-shadow"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>{t('privacy.backToLogin')}</span>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PrivacyPolicy;
